import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import {
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";

import { BookingOwnerChip } from "components/Carrier/components/BookingOwnerChip";
import { SharedBookingChip } from "components/Carrier/components/SharedBookingChip";
import { ConvertedPriceTooltip } from "components/ConvertedPriceTooltip";
import { QuoteStatusBadge } from "components/QuoteStatusBadge";
import { ShipperViewedIndicator } from "components/ShipperViewedIndicator";
import { ShipmentLocationText } from "components/Texts/ShipmentLocationText";
import { ShipmentPriceText } from "components/Texts/ShipmentPriceText";
import { EVOTRUX_BLUE, EVOTRUX_GREEN } from "constants/index";
import {
  formatText,
  isAcceptedTerminalQuote,
  ISOStringToShorthandDate,
  toCityProvinceCountryAndZipReadablePointLocation,
} from "utils";

import { OptionalShipmentStatusBadge } from "./components/OptionalShipmentStatusBadge";
import { QuotePreviewShipmentIDText } from "./components/QuotePreviewShipmentIDText";

interface Props {
  quote: CondensedQuote;
  cadPerUsd: ExchangeRate | null;
  usdPerCad: ExchangeRate | null;
}

function getQuoteStatusBorderColor(
  status: InternalQuoteStatusValue,
): string | undefined {
  switch (status) {
    case "ACTIVE":
    case "COUNTERED":
      return EVOTRUX_BLUE;
    case "ACCEPTED":
      return EVOTRUX_GREEN;
    default:
      return undefined;
  }
}

export function QuotePreview(props: Props) {
  const { quote, cadPerUsd, usdPerCad } = props;

  const equipmentTypeLabel = formatText(quote.equipment_type);
  const statusBorderColor = getQuoteStatusBorderColor(quote.internal_state);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: 2 },
        ...(statusBorderColor && {
          borderLeft: `4px solid ${statusBorderColor}`,
        }),
      }}
    >
      <CardActionArea
        component={Link}
        to={`/shipments/${quote.shipment}?focus=quotes`}
      >
        <CardHeader
          sx={{ pb: 0 }}
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <QuotePreviewShipmentIDText quote={quote} />
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {isAcceptedTerminalQuote(quote) ? (
                  <OptionalShipmentStatusBadge
                    shipmentStatus={quote.shipment_status}
                    size="small"
                  />
                ) : (
                  <QuoteStatusBadge
                    status={quote.internal_state ?? "unknown"}
                    size="small"
                    isHidden={quote.hidden_at != null}
                  />
                )}
                <SharedBookingChip
                  carrieAccessType={quote.carrier_access_type}
                />
              </Stack>
            </Stack>
          }
          action={
            <ShipperViewedIndicator
              quoteId={quote.quote}
              viewedAt={quote.last_viewed_by_shipper}
              hideFromNonSubscribed
            />
          }
        />
        <CardContent>
          <Stack direction="column" spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <ConvertedPriceTooltip
                price={quote.price}
                currencyCode={quote.currency}
                cadPerUsd={cadPerUsd}
                usdPerCad={usdPerCad}
              >
                <ShipmentPriceText
                  price={quote.price}
                  currency={quote.currency}
                  quotingUnitOfMeasure={quote.quoting_unit_of_measure}
                  variant="body1"
                  sx={{ fontWeight: 600 }}
                />
              </ConvertedPriceTooltip>
              <Chip
                label={equipmentTypeLabel}
                variant="outlined"
                size="small"
                sx={{ textTransform: "capitalize", flexShrink: 0 }}
              />
            </Stack>

            <Stack direction="column" sx={{ width: "100%" }} spacing={0.5}>
              {quote.carrier_access_type ===
                "SHARED_BOOKING_READ_AND_WRITE" && (
                <Stack direction="row" alignSelf="flex-end">
                  <BookingOwnerChip carrierSharees={quote.carrier_sharees} />
                </Stack>
              )}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Stack direction="column" alignItems="center">
                  <ShipmentLocationText
                    data={toCityProvinceCountryAndZipReadablePointLocation(
                      quote.origin_location,
                      false,
                      false,
                    )}
                    icon={
                      <TripOriginIcon
                        sx={{ fontSize: "0.9rem", marginRight: "5px" }}
                      />
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    {ISOStringToShorthandDate(quote.pickup_start)}
                  </Typography>
                </Stack>
                <ArrowRightAltIcon sx={{ color: "text.secondary" }} />
                <Stack id="destination" direction="column" alignItems="center">
                  <ShipmentLocationText
                    data={toCityProvinceCountryAndZipReadablePointLocation(
                      quote.destination_location,
                      false,
                      false,
                    )}
                    icon={
                      <FmdGoodIcon
                        sx={{ fontSize: "0.9rem", marginRight: "5px" }}
                      />
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    {ISOStringToShorthandDate(quote.delivery_eta)}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
